import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { Box, Button, Typography } from '@mui/material';
import ReplayIcon from '@mui/icons-material/Replay';
import {
  calculateWpm,
  calculateAccuracy,
  calculateNetWpm,
  formatDuration,
} from './typingTutorUtils';
import { buildWordStream, getComboMultiplier } from './typingTutorGames';

/**
 * Monkeytype-style timed word sprint — type words + space to advance
 */
export default function SpeedBlitzGame({
  durationSeconds = 60,
  targetWpm = 35,
  onComplete,
  onRetry,
  onCancel,
}) {
  const words = useMemo(() => buildWordStream(250), []);
  const [wordIndex, setWordIndex] = useState(0);
  const [typed, setTyped] = useState('');
  const [started, setStarted] = useState(false);
  const [finished, setFinished] = useState(false);
  const [remaining, setRemaining] = useState(durationSeconds);
  const [result, setResult] = useState(null);
  const [combo, setCombo] = useState(0);
  const [bestCombo, setBestCombo] = useState(0);
  const [wordsCompleted, setWordsCompleted] = useState(0);
  const [correctChars, setCorrectChars] = useState(0);
  const [totalTyped, setTotalTyped] = useState(0);
  const inputRef = useRef(null);
  const timerRef = useRef(null);
  const statsRef = useRef({ correctChars: 0, totalTyped: 0, wordsCompleted: 0, bestCombo: 0 });

  const currentWord = words[wordIndex] || '';

  const reset = useCallback(() => {
    setWordIndex(0);
    setTyped('');
    setStarted(false);
    setFinished(false);
    setRemaining(durationSeconds);
    setResult(null);
    setCombo(0);
    setBestCombo(0);
    setWordsCompleted(0);
    setCorrectChars(0);
    setTotalTyped(0);
    statsRef.current = { correctChars: 0, totalTyped: 0, wordsCompleted: 0, bestCombo: 0 };
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
    onRetry?.();
    setTimeout(() => inputRef.current?.focus(), 50);
  }, [durationSeconds, onRetry]);

  useEffect(() => {
    reset();
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [durationSeconds, reset]);

  const finishGame = useCallback(() => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
    const elapsed = durationSeconds - remaining;
    const timeSeconds = Math.max(1, elapsed);
    const { correctChars: cc, totalTyped: tt, wordsCompleted: wc, bestCombo: bc } = statsRef.current;
    const wpm = calculateWpm(cc, timeSeconds);
    const accuracy = calculateAccuracy(cc, tt);
    const netWpm = calculateNetWpm(cc, tt, timeSeconds);
    const sessionResult = {
      wpm,
      netWpm,
      accuracy,
      timeSpentSeconds: timeSeconds,
      errors: tt - cc,
      totalChars: tt,
      correctChars: cc,
      wordsCompleted: wc,
      bestCombo: bc,
      goalMet: wpm >= targetWpm,
    };
    setResult(sessionResult);
    setFinished(true);
    onComplete?.(sessionResult);
  }, [durationSeconds, remaining, targetWpm, onComplete]);

  useEffect(() => {
    if (!started || finished) return undefined;
    timerRef.current = setInterval(() => {
      setRemaining((prev) => {
        if (prev <= 1) {
          setTimeout(finishGame, 0);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [started, finished, finishGame]);

  const advanceWord = useCallback((finalTyped) => {
    let wordCorrect = true;
    for (let i = 0; i < finalTyped.length; i += 1) {
      if (finalTyped[i] !== currentWord[i]) wordCorrect = false;
    }
    const charsInWord = finalTyped.length + 1;
    let wordCorrectChars = 0;
    for (let i = 0; i < finalTyped.length; i += 1) {
      if (finalTyped[i] === currentWord[i]) wordCorrectChars += 1;
    }
    wordCorrectChars += 1;

    statsRef.current.correctChars += wordCorrectChars;
    statsRef.current.totalTyped += charsInWord;
    statsRef.current.wordsCompleted += 1;
    setCorrectChars(statsRef.current.correctChars);
    setTotalTyped(statsRef.current.totalTyped);
    setWordsCompleted(statsRef.current.wordsCompleted);

    if (wordCorrect) {
      setCombo((prev) => {
        const next = prev + 1;
        if (next > statsRef.current.bestCombo) {
          statsRef.current.bestCombo = next;
          setBestCombo(next);
        }
        return next;
      });
    } else {
      setCombo(0);
    }

    setTyped('');
    setWordIndex((prev) => prev + 1);
  }, [currentWord]);

  useEffect(() => {
    const handleKey = (e) => {
      if (finished) return;
      if (e.ctrlKey || e.metaKey || e.altKey) return;
      if (e.key === 'Escape') {
        onCancel?.();
        return;
      }
      if (e.key === 'Backspace') {
        e.preventDefault();
        setTyped((prev) => prev.slice(0, -1));
        setCombo(0);
        return;
      }
      if (e.key !== ' ' && e.key.length !== 1) return;
      e.preventDefault();

      if (!started) setStarted(true);

      if (e.key === ' ') {
        if (typed.length > 0) advanceWord(typed);
        return;
      }

      setTyped((prev) => {
        if (prev.length >= currentWord.length) return prev;
        const next = prev + e.key;
        const isCorrect = e.key === currentWord[prev.length];
        statsRef.current.totalTyped += 1;
        if (isCorrect) statsRef.current.correctChars += 1;
        setTotalTyped(statsRef.current.totalTyped);
        setCorrectChars(statsRef.current.correctChars);

        if (!isCorrect) setCombo(0);
        else if (next.length === currentWord.length) {
          setTimeout(() => advanceWord(next), 0);
        }
        return next;
      });
    };

    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [finished, started, typed, currentWord, advanceWord, onCancel]);

  const elapsed = durationSeconds - remaining;
  const liveWpm = started ? calculateWpm(correctChars, Math.max(1, elapsed)) : 0;
  const liveAccuracy = totalTyped > 0 ? calculateAccuracy(correctChars, totalTyped) : 100;
  const multiplier = getComboMultiplier(combo);

  const renderWord = (word, idx) => {
    const isCurrent = idx === wordIndex;
    const isPast = idx < wordIndex;
    return (
      <span
        key={`${word}-${idx}`}
        className={`tt-blitz-word ${isCurrent ? 'tt-blitz-word-current' : ''} ${isPast ? 'tt-blitz-word-done' : ''}`}
      >
        {isCurrent
          ? word.split('').map((ch, i) => {
              let cls = 'tt-char-pending';
              if (i < typed.length) {
                cls = typed[i] === ch ? 'tt-char-correct' : 'tt-char-incorrect';
              } else if (i === typed.length) {
                cls += ' tt-char-current';
              }
              return (
                <span key={i} className={cls}>
                  {ch}
                </span>
              );
            })
          : word}
      </span>
    );
  };

  if (finished && result) {
    return (
      <Box className="tt-result-panel tt-card">
        <Typography variant="h5" className="tt-sans" fontWeight={700} gutterBottom>
          Speed Blitz Complete
        </Typography>
        <Box className="tt-stats-bar" sx={{ justifyContent: 'center', mb: 2, mt: 2 }}>
          <div className="tt-stat-item">
            <div className="tt-stat-value">{result.wpm}</div>
            <div className="tt-stat-label">WPM</div>
          </div>
          <div className="tt-stat-item">
            <div className="tt-stat-value">{result.netWpm}</div>
            <div className="tt-stat-label">Net WPM</div>
          </div>
          <div className="tt-stat-item">
            <div className="tt-stat-value">{result.accuracy}%</div>
            <div className="tt-stat-label">Accuracy</div>
          </div>
          <div className="tt-stat-item">
            <div className="tt-stat-value">{result.wordsCompleted}</div>
            <div className="tt-stat-label">Words</div>
          </div>
          <div className="tt-stat-item">
            <div className="tt-stat-value">{result.bestCombo}</div>
            <div className="tt-stat-label">Best Combo</div>
          </div>
        </Box>
        <Typography className="tt-sans" sx={{ color: result.goalMet ? 'success.main' : 'text.secondary', mb: 2 }}>
          {result.goalMet
            ? `Excellent! You hit the ${targetWpm} WPM goal.`
            : `Keep pushing — goal is ${targetWpm} WPM.`}
        </Typography>
        <Button variant="outlined" startIcon={<ReplayIcon />} onClick={reset} sx={{ mr: 1 }}>
          Play Again
        </Button>
        <Button variant="contained" color="success" onClick={onCancel}>
          Back to Games
        </Button>
      </Box>
    );
  }

  const timerPct = (remaining / durationSeconds) * 100;

  return (
    <Box>
      <Box className="tt-stats-bar" sx={{ mb: 2 }}>
        <div className="tt-stat-item">
          <div className="tt-stat-value">{liveWpm}</div>
          <div className="tt-stat-label">WPM</div>
        </div>
        <div className="tt-stat-item">
          <div className="tt-stat-value">{liveAccuracy}%</div>
          <div className="tt-stat-label">Accuracy</div>
        </div>
        <div className="tt-stat-item">
          <div className="tt-stat-value">{formatDuration(remaining)}</div>
          <div className="tt-stat-label">Left</div>
        </div>
        <div className="tt-stat-item">
          <div className="tt-stat-value">{wordsCompleted}</div>
          <div className="tt-stat-label">Words</div>
        </div>
        {combo >= 3 && (
          <div className="tt-stat-item tt-combo-stat">
            <div className="tt-stat-value">×{multiplier.toFixed(1)}</div>
            <div className="tt-stat-label">{combo} combo</div>
          </div>
        )}
      </Box>

      <div className="tt-progress-track tt-blitz-timer" style={{ marginBottom: '1rem' }}>
        <div
          className="tt-progress-fill tt-timer-fill"
          style={{ width: `${timerPct}%` }}
        />
      </div>

      <div
        className="tt-typing-area tt-mono tt-blitz-area"
        onClick={() => inputRef.current?.focus()}
        role="textbox"
        tabIndex={0}
      >
        {words.slice(wordIndex, wordIndex + 15).map((w, i) => renderWord(w, wordIndex + i))}
      </div>

      <input ref={inputRef} className="tt-hidden-input" autoFocus aria-hidden tabIndex={-1} />

      <Typography className="tt-keyboard-hint tt-sans">
        {!started
          ? 'Start typing — timer begins on first key. Press Space after each word.'
          : 'Type the highlighted word, then Space. Esc to exit.'}
      </Typography>
    </Box>
  );
}
